import mainApi from "../../api/mainApi";
import { CommoditiesResponse } from "../../interfaces/costs/costs.commodities.interface";
import { ContainersResponse } from "../../interfaces/costs/costs.containers.interface";
import { CostsResponse } from "../../interfaces/costs/costs.interface";
import { DestinationsResponse } from "../../interfaces/costs/costs.origins.destinations";
import { OriginsResponse } from "../../interfaces/costs/costs.origins.interface";
import { ProvidersResponse } from "../../interfaces/costs/costs.providers.interface";
import { RulesResponse } from "../../interfaces/costs/costs.rules.interface";
import { middlewareApi } from "../../middlewares/middlewareApi";

const costsService = middlewareApi
  .enhanceEndpoints({ addTagTypes: ["Cost", "Commodity"] })
  // const costsService = middlewareApi.injectEndpoints
  .injectEndpoints({
    endpoints: (builder) => ({
      /**
       * @GET get list providers
       */
      getProviders: builder.query<ProvidersResponse, void>({
        queryFn: async () => {
          try {
            const { data } = await mainApi.get<ProvidersResponse>(
              "costs/getProviders"
            );
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
      }),
      /**
       * @GET get list origins
       */
      getOrigins: builder.query<OriginsResponse, void>({
        queryFn: async () => {
          try {
            const { data } = await mainApi.get<OriginsResponse>(
              "costs/getOrigins"
            );
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
      }),
      /**
       * @GET get list destinations
       */
      getDestinations: builder.query<DestinationsResponse, void>({
        queryFn: async () => {
          try {
            const { data } = await mainApi.get<DestinationsResponse>(
              "costs/getDestinations"
            );
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
      }),
      /**
       * @GET get list containers
       */
      getContainers: builder.query<ContainersResponse, void>({
        queryFn: async () => {
          try {
            const { data } = await mainApi.get<ContainersResponse>(
              "costs/getContainers"
            );
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
      }),
      /**
       * @GET list commodities
       */
      getCommodities: builder.query<CommoditiesResponse, void>({
        queryFn: async () => {
          try {
            const { data } = await mainApi.get("costs/getCommodities");
            return { data };
          } catch (error: any) {
            return { error: error };
          }
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.datos.map(({ id_commodity }) => ({
                  type: "Commodity" as const,
                  id_commodity,
                })),
                "Commodity",
              ]
            : ["Commodity"],
      }),
      /**
       * @GET get lists costs
       */
      getCosts: builder.query<
        CostsResponse,
        {
          provider: string;
          origin: string;
          destination: string;
          container: string;
        }
      >({
        queryFn: async ({ provider, origin, destination, container }) => {
          try {
            const { data } = await mainApi.get<CostsResponse>(
              "costs/getCosts",
              {
                params: {
                  provider,
                  origin,
                  destination,
                  container,
                },
              }
            );
            return { data };
          } catch (error: any) {
            return { error };
          }
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.datos.map(({ id_tarifa }) => ({
                  type: "Cost" as const,
                  id_tarifa,
                })),
                "Cost",
              ]
            : ["Cost"],
      }),
      processFile: builder.mutation<
        string,
        {
          files: File[];
        }
      >({
        queryFn: async ({ files }) => {
          try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
              formData.append("file[]", files[i]);
            }
            const { data } = await mainApi.post<any>(
              "costs/processFile",
              formData,
              {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 100000,
              }
            );
            // return { data };
            return { data: data?.mensaje || "[[Successfully]]" };
          } catch (error: any) {
            return {
              error:
                error.response?.data?.mensaje ||
                "Ocurrio un error inesperado. Intente luego.",
            };
          }
        },
      }),
      /**
       * @GET list rules
       */
      getRules: builder.query<
        RulesResponse,
        {
          page: number;
          size: number;
          origen?: string;
          destino?: string;
          naviero?: string;
          commodity?: string;
          contenedor?: string;
          contract?: string;
        }
      >({
        queryFn: async ({
          page,
          size,
          origen,
          destino,
          naviero,
          commodity,
          contenedor,
          contract,
        }) => {
          try {
            const { data } = await mainApi.get("costs/getRules", {
              params: {
                page,
                size,
                origen,
                destino,
                naviero,
                commodity,
                contenedor,
                contract,
              },
            });
            return { data };
          } catch (error: any) {
            return { error: error };
          }
        },
      }),
      /**
       * @POST create rule
       */
      createRule: builder.mutation<
        any,
        {
          origen: string;
          destino: string;
          commodity: string;
          contenedor: string;
          contract_number: string;
          notes: string;
          naviero: string;
        }
      >({
        queryFn: async ({
          origen,
          destino,
          commodity,
          contenedor,
          contract_number,
          notes,
          naviero,
        }) => {
          try {
            const { data } = await mainApi.post("costs/createRule", {
              origen,
              destino,
              commodity,
              contenedor,
              contract_number,
              notes,
              naviero,
            });
            return { data };
          } catch (error: any) {
            return { error: error };
          }
        },
      }),
      /**
       * @POST disable rule
       */
      disableRule: builder.mutation<
        any,
        {
          id_regla: number;
        }
      >({
        queryFn: async ({ id_regla }) => {
          try {
            const { data } = await mainApi.post("costs/disableRule", {
              id_regla,
            });
            return { data };
          } catch (error: any) {
            return { error: error };
          }
        },
      }),
      /**
       * @POST  update tarifa's notes
       */
      updateNotes: builder.mutation<
        any,
        {
          idTarifa: number;
          notes: string | null;
        }
      >({
        queryFn: async ({ idTarifa, notes }) => {
          try {
            const { data } = await mainApi.post<any>("costs/updateNotes", {
              idTarifa,
              notes,
            });
            return { data };
          } catch (error: any) {
            return {
              error: {
                status: 500,
                data: error.response.data.mensaje,
              },
            };
          }
        },
        invalidatesTags: (result, error) => (result ? ["Cost"] : []),
      }),
      /**
       * @POST Actualiza regla
       */
      updateRule: builder.mutation<
        any,
        {
          origen: string;
          destino: string;
          commodity: string;
          contenedor: string;
          contract_number: string;
          notes: string;
          id_regla: number;
          naviero: string;
        }
      >({
        queryFn: async ({
          commodity,
          contenedor,
          contract_number,
          destino,
          id_regla,
          notes,
          origen,
          naviero,
        }) => {
          try {
            const { data } = await mainApi.post("costs/updateRule", {
              commodity,
              contenedor,
              contract_number,
              destino,
              id_regla,
              notes,
              origen,
              naviero,
            });
            return { data };
          } catch (error: any) {
            return { error: error };
          }
        },
      }),
      /**
       * @POST create commodity
       */
      createCommodity: builder.mutation<
        any,
        {
          nombre: string;
          descripcion: string;
        }
      >({
        queryFn: async ({ nombre, descripcion }) => {
          try {
            const { data } = await mainApi.post("costs/createCommodity", {
              nombre,
              descripcion,
            });
            return { data };
          } catch (error: any) {
            return { error: error.response.data.msg || String(error) };
          }
        },
        invalidatesTags: ["Commodity"],
      }),
    }),
  });

export const {
  useGetProvidersQuery,
  useGetOriginsQuery,
  useGetDestinationsQuery,
  useGetContainersQuery,
  useGetCommoditiesQuery,
  useLazyGetCostsQuery,
  useProcessFileMutation,
  useUpdateNotesMutation,
  useGetRulesQuery,
  useCreateRuleMutation,
  useDisableRuleMutation,
  useUpdateRuleMutation,
  useCreateCommodityMutation,
  useLazyGetRulesQuery,
} = costsService;
